import type { ReactNode } from 'react';
import { memo, useMemo } from 'react';
import { MetricBulletChart, type BandOverlay, type MetricBulletChartUnit } from './MetricBulletChart';
import { computePeerFullDomain } from '@/shared/utils/domain';
import type { PeerGroupMetrics } from '@/features/reports/utils/peerGroupMetrics';

export interface TuitionBandOverlayInput {
  min: number;
  max: number;
  label: string;
}

export interface PeerMetricPanelProps {
  title: string;
  peer: PeerGroupMetrics;
  tuitionBandOverlay?: TuitionBandOverlayInput;
  parentTuitionBandOverlay?: TuitionBandOverlayInput;
  unit?: MetricBulletChartUnit;
  className?: string;
  costHeaderRight?: ReactNode;
  onOpenBudget?: (budgetId: string, meta?: { schoolName?: string }) => void;
}

const cardClass =
  'rounded-lg border border-slate-200/90 bg-white p-3 shadow-sm ring-1 ring-slate-100 print:border-slate-300 print:p-1.5 print:shadow-none';

const PANEL_HELP_BASE =
  'Shows where this school sits among similar schools. The scale spans the full peer min–max range (with slight padding); the dashed band is typical peers (~p10–p90). Colored overlays on tuition show governed bands where applicable.';

function peerRankBadge(kind: 'cost' | 'tuition', rank: number, n: number) {
  const label = kind === 'cost' ? 'Cost rank' : 'Tuition rank';
  const denom = n > 0 ? ` / ${n.toLocaleString()}` : '';
  return (
    <span
      className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-700 shadow-sm print:px-1 print:py-0.5 print:text-[6px]"
      title={`This school’s ${kind === 'cost' ? 'cost per student' : 'tuition per student'} rank within this peer group. Rank 1 = highest ${kind === 'cost' ? 'cost' : 'tuition'} among peers (API: ${kind}Rank).`}
    >
      {label} {rank.toLocaleString()}
      {denom}
    </span>
  );
}

function peerRankHeaderEl(
  kind: 'cost' | 'tuition',
  rank: number | undefined,
  n: number
): ReactNode {
  if (rank == null || !Number.isFinite(rank)) return null;
  const r = Math.round(rank);
  if (r < 1) return null;
  return peerRankBadge(kind, r, n);
}

function peerRankAriaNote(kind: 'cost' | 'tuition', rank: number | undefined, n: number): string | undefined {
  if (rank == null || !Number.isFinite(rank) || n < 1) return undefined;
  const r = Math.round(rank);
  if (r < 1) return undefined;
  const noun = kind === 'cost' ? 'cost' : 'tuition';
  return `${noun} rank ${r} of ${n}; rank 1 is highest ${noun} among peers`;
}

function typicalBandFromDisplay(
  dMin: number,
  dMax: number
): { min: number; max: number } | undefined {
  if (!Number.isFinite(dMin) || !Number.isFinite(dMax) || !(dMax > dMin)) return undefined;
  return { min: dMin, max: dMax };
}

function tuitionOverlays(
  peer: PeerGroupMetrics,
  tuitionBandOverlay?: TuitionBandOverlayInput,
  parentTuitionBandOverlay?: TuitionBandOverlayInput
): BandOverlay[] {
  const overlays: BandOverlay[] = [];
  if (
    peer.bandTuitionMin != null &&
    peer.bandTuitionMax != null &&
    Number.isFinite(peer.bandTuitionMin) &&
    Number.isFinite(peer.bandTuitionMax) &&
    peer.bandTuitionMax > peer.bandTuitionMin
  ) {
    overlays.push({
      min: peer.bandTuitionMin,
      max: peer.bandTuitionMax,
      label: 'Tuition band',
      style: 'solid',
    });
  }
  if (
    tuitionBandOverlay &&
    Number.isFinite(tuitionBandOverlay.min) &&
    Number.isFinite(tuitionBandOverlay.max) &&
    tuitionBandOverlay.max > tuitionBandOverlay.min
  ) {
    overlays.push({ ...tuitionBandOverlay, style: 'solid' });
  }
  if (
    parentTuitionBandOverlay &&
    Number.isFinite(parentTuitionBandOverlay.min) &&
    Number.isFinite(parentTuitionBandOverlay.max) &&
    parentTuitionBandOverlay.max > parentTuitionBandOverlay.min
  ) {
    overlays.push({ ...parentTuitionBandOverlay, style: 'dashed' });
  }
  return overlays;
}

/**
 * Cost + tuition absolute charts for one peer band (typical peer range, optional tuition band overlays).
 */
function PeerMetricPanelInner({
  title,
  peer,
  tuitionBandOverlay,
  parentTuitionBandOverlay,
  unit = 'USD',
  className = '',
  costHeaderRight,
  onOpenBudget,
}: PeerMetricPanelProps) {
  const n = peer.schoolCount;
  const panelHelp =
    PANEL_HELP_BASE +
    (onOpenBudget
      ? ' Markers with a blue ring or a “Budget” label open the detailed budget summary when a budget id is available.'
      : '');
  const drill = useMemo(
    () => ({
      actualBudgetId: peer.budgetId,
      minBudgetId: peer.minBudgetId,
      maxBudgetId: peer.maxBudgetId,
      minBudgetSchoolName: peer.minBudgetSchoolName,
      maxBudgetSchoolName: peer.maxBudgetSchoolName,
    }),
    [peer.budgetId, peer.minBudgetId, peer.maxBudgetId, peer.minBudgetSchoolName, peer.maxBudgetSchoolName]
  );

  const costScale = useMemo(() => {
    const [cMin, cMax] = computePeerFullDomain(
      peer.minCostPerStudentUSD,
      peer.maxCostPerStudentUSD,
      peer.costPerStudentUSD,
      peer.avgCostPerStudentUSD,
      peer.medianCostPerStudentUSD
    );
    return { cMin, cMax };
  }, [peer]);

  const tuitionScale = useMemo(() => {
    const [tMin, tMax] = computePeerFullDomain(
      peer.minTuitionPerStudentUSD,
      peer.maxTuitionPerStudentUSD,
      peer.tuitionPerStudentUSD,
      peer.avgTuitionPerStudentUSD,
      peer.medianTuitionPerStudentUSD
    );
    return { tMin, tMax };
  }, [peer]);

  const costTypicalBand = useMemo(
    () => typicalBandFromDisplay(peer.displayMinCostPerStudentUSD, peer.displayMaxCostPerStudentUSD),
    [peer.displayMinCostPerStudentUSD, peer.displayMaxCostPerStudentUSD]
  );

  const tuitionTypicalBand = useMemo(
    () => typicalBandFromDisplay(peer.displayMinTuitionPerStudentUSD, peer.displayMaxTuitionPerStudentUSD),
    [peer.displayMinTuitionPerStudentUSD, peer.displayMaxTuitionPerStudentUSD]
  );

  const costRankAria = useMemo(() => peerRankAriaNote('cost', peer.costRank, n), [peer.costRank, n]);
  const tuitionRankAria = useMemo(() => peerRankAriaNote('tuition', peer.tuitionRank, n), [peer.tuitionRank, n]);

  const tuitionOv = useMemo(
    () => tuitionOverlays(peer, tuitionBandOverlay, parentTuitionBandOverlay),
    [peer, tuitionBandOverlay, parentTuitionBandOverlay]
  );

  const nearBounds = useMemo(() => {
    const nb = {
      peerMinInBand: peer.peerMinInBandTuition,
      peerMaxInBand: peer.peerMaxInBandTuition,
      closestBelowSchool: peer.closestBelowSchoolTuition,
      closestAboveSchool: peer.closestAboveSchoolTuition,
    };
    const any =
      (nb.peerMinInBand != null && Number.isFinite(nb.peerMinInBand)) ||
      (nb.peerMaxInBand != null && Number.isFinite(nb.peerMaxInBand)) ||
      (nb.closestBelowSchool != null && Number.isFinite(nb.closestBelowSchool)) ||
      (nb.closestAboveSchool != null && Number.isFinite(nb.closestAboveSchool));
    return any ? nb : undefined;
  }, [peer]);

  const costRankEl = peerRankHeaderEl('cost', peer.costRank, n);
  const tuitionRankEl = peerRankHeaderEl('tuition', peer.tuitionRank, n);
  const costHeaderMerged =
    costRankEl || costHeaderRight ? (
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {costRankEl}
        {costHeaderRight}
      </div>
    ) : undefined;

  return (
    <div
      className={`rounded-xl border border-slate-300/80 bg-slate-50/80 p-4 shadow-sm ring-1 ring-slate-200/80 print:border-slate-400 print:p-2 print:shadow-none ${className}`}
    >
      <div className="mb-3 border-b border-slate-200/90 pb-3 print:mb-2 print:pb-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="min-w-0 text-sm font-bold text-slate-900 print:text-[9px]">{title}</h4>
          <span
            className="inline-flex shrink-0 items-center rounded-md border border-slate-700/20 bg-slate-800 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm print:px-1.5 print:py-0.5 print:text-[7px]"
            title="Schools in peer group"
          >
            N = {n.toLocaleString()}
          </span>
        </div>
        <p className="mt-2 max-w-3xl text-[10px] leading-snug text-slate-600 print:mt-1 print:text-[7px]">{panelHelp}</p>
      </div>
      <div className="flex flex-col gap-5 print:gap-3">
        <div className={cardClass}>
          <MetricBulletChart
            label="Cost per student (USD)"
            unit={unit}
            domainMin={costScale.cMin}
            domainMax={costScale.cMax}
            actual={peer.costPerStudentUSD}
            avg={peer.avgCostPerStudentUSD}
            median={peer.medianCostPerStudentUSD}
            rawMin={peer.minCostPerStudentUSD}
            rawMax={peer.maxCostPerStudentUSD}
            n={n}
            typicalPeerBand={costTypicalBand}
            expandedNote={false}
            peerDistributionScale
            headerRight={costHeaderMerged}
            drill={drill}
            onOpenBudget={onOpenBudget}
            rankAriaNote={costRankAria}
          />
        </div>
        <div className={cardClass}>
          <MetricBulletChart
            label="Tuition (USD / student)"
            unit={unit}
            domainMin={tuitionScale.tMin}
            domainMax={tuitionScale.tMax}
            actual={peer.tuitionPerStudentUSD}
            avg={peer.avgTuitionPerStudentUSD}
            median={peer.medianTuitionPerStudentUSD}
            rawMin={peer.minTuitionPerStudentUSD}
            rawMax={peer.maxTuitionPerStudentUSD}
            n={n}
            overlays={tuitionOv.length ? tuitionOv : undefined}
            nearBounds={nearBounds}
            typicalPeerBand={tuitionTypicalBand}
            expandedNote={false}
            peerDistributionScale
            headerRight={tuitionRankEl || undefined}
            drill={drill}
            onOpenBudget={onOpenBudget}
            rankAriaNote={tuitionRankAria}
          />
        </div>
      </div>
    </div>
  );
}

export const PeerMetricPanel = memo(PeerMetricPanelInner);
